import React, { useRef, useEffect, useMemo, useState } from 'react';
import type { AssistantUITheme } from '@adsk/adp-web-analytics-sdk/lib/AssistantUI';
import { useSiteConfig, useWeaveThemeContext } from '@innovyze/stylovyze';
import { useAssistantMessaging } from './useAssistantMessaging';
import { useAssistantAdpClient } from './useAssistantAdpClient';
import { info360ToAssistantTheme } from './info360ToAssistantTheme';

type AssistantEnv = 'stg' | 'prd';

// Info360 signs users in through Autodesk production only in prod; every other environment uses staging.
export const getAssistantEnv = (adpEnv: string | undefined): AssistantEnv =>
  adpEnv === 'prd' ? 'prd' : 'stg';

const assistantThemesEqual = (
  a: AssistantUITheme,
  b: AssistantUITheme | null,
): boolean => b !== null && a.color === b.color && a.density === b.density;

const applyAssistantOverrides = (iframe: HTMLIFrameElement) => {
  const url = process.env.REACT_APP_ASSISTANT_URL;
  const permissions = process.env.REACT_APP_ASSISTANT_PERMISSIONS;
  if (!url) return;

  if (permissions) {
    iframe.allow = permissions;
  }
  iframe.src = url;
};

const AutodeskAssistant = () => {
  useAssistantMessaging();
  const adpClient = useAssistantAdpClient();
  const { theme: info360Theme } = useWeaveThemeContext();
  const { config } = useSiteConfig();
  const assistantEnv = getAssistantEnv(config?.adp?.env);
  const assistantTheme = useMemo(
    () => info360ToAssistantTheme(info360Theme),
    [info360Theme],
  );

  const assistantThemeRef = useRef(assistantTheme);
  assistantThemeRef.current = assistantTheme;

  const lastSdkThemeRef = useRef<AssistantUITheme | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [isAssistantEmbedded, setIsAssistantEmbedded] = useState(false);

  useEffect(() => {
    const uiHost = containerRef.current;
    if (!uiHost || !adpClient || isAssistantEmbedded) return;

    const scheduleId = window.setTimeout(() => {
      void (async () => {
        try {
          const themeAtEmbed = assistantThemeRef.current;
          await adpClient.showAssistant({
            container: uiHost,
            // The panel page loads AA in whichever environment `env` names.
            urlSearchParameters: { notitle: 'true', env: assistantEnv },
            theme: themeAtEmbed,
          });
          lastSdkThemeRef.current = themeAtEmbed;
          const iframe = uiHost.querySelector(
            'iframe.adp-assistant',
          ) as HTMLIFrameElement | null;
          if (iframe) {
            applyAssistantOverrides(iframe);
          }
          setIsAssistantEmbedded(true);
        } catch (error: unknown) {
          console.error('[Bernoulli] showAssistant failed', error);
        }
      })();
    }, 1000); // FIXME: Remove this delay once we have a proper way to detect when the assistant is ready

    return () => {
      window.clearTimeout(scheduleId);
    };
  }, [adpClient, assistantEnv, isAssistantEmbedded]);

  useEffect(() => {
    if (!adpClient || !isAssistantEmbedded) return;

    const next = assistantTheme;
    if (assistantThemesEqual(next, lastSdkThemeRef.current)) {
      return;
    }

    lastSdkThemeRef.current = next;
    void adpClient.setAssistantTheme(next).catch((error: unknown) => {
      console.error('[Bernoulli] setAssistantTheme failed', error);
    });
  }, [adpClient, assistantTheme, isAssistantEmbedded]);

  return (
    <div
      ref={containerRef}
      style={{
        height: '100%',
        width: '100%',
      }}
    />
  );
};

export default AutodeskAssistant;
